import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Ban,
  Calendar,
  Check,
  CheckCircle,
  CreditCard,
  DollarSign,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Star,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import {
  type ApiUser,
  type UserProfile,
  getProfileDisplayLabel,
  profileTypeColors,
  profileTypeIcons,
  profileTypeLabels,
  roleColors,
  roleIcons,
  roleLabels,
} from '../model';

interface AgentDetails {
  id: string;
  commission_rate_equipment: number | null;
  commission_rate_subs: number | null;
  territory_name: string | null;
}

interface PaymentDetails {
  stripe_customer_id: string | null;
  subscription_status: string | null;
}

export function UserDetailsDialog({
  showViewModal,
  selectedUser,
  setShowViewModal,
  formatDate,
  editingPhone,
  phoneInput,
  setPhoneInput,
  handleSavePhone,
  isSavingPhone,
  setEditingPhone,
  userPhone,
  agentDetails,
  editingCommission,
  setCommissionEquipInput,
  setCommissionSubsInput,
  setEditingCommission,
  commissionEquipInput,
  commissionSubsInput,
  handleSaveCommission,
  isSavingCommission,
  paymentDetails,
  setAddProfileType,
  setHqAdminConfirmText,
  setAddProfileTerritoryName,
  setAddProfileAffiliateId,
  setAddProfileMemberRole,
  setShowAddProfileModal,
  isLoadingProfiles,
  userProfiles,
  handleReactivateProfile,
  removingProfileId,
  handleSetPrimaryProfile,
  settingPrimaryId,
  handleRemoveProfile,
}: {
  showViewModal: boolean;
  selectedUser: ApiUser | null;
  setShowViewModal: (value: boolean) => void;
  formatDate: (value: string) => string;
  editingPhone: boolean;
  phoneInput: string;
  setPhoneInput: (value: string) => void;
  handleSavePhone: () => Promise<void>;
  isSavingPhone: boolean;
  setEditingPhone: (value: boolean) => void;
  userPhone: string | null;
  agentDetails: AgentDetails | null;
  editingCommission: boolean;
  setCommissionEquipInput: (value: string) => void;
  setCommissionSubsInput: (value: string) => void;
  setEditingCommission: (value: boolean) => void;
  commissionEquipInput: string;
  commissionSubsInput: string;
  handleSaveCommission: () => Promise<void>;
  isSavingCommission: boolean;
  paymentDetails: PaymentDetails | null;
  setAddProfileType: (value: string) => void;
  setHqAdminConfirmText: (value: string) => void;
  setAddProfileTerritoryName: (value: string) => void;
  setAddProfileAffiliateId: (value: string) => void;
  setAddProfileMemberRole: (value: 'owner' | 'coach' | 'member') => void;
  setShowAddProfileModal: (value: boolean) => void;
  isLoadingProfiles: boolean;
  userProfiles: UserProfile[];
  handleReactivateProfile: (profileId: string) => Promise<void>;
  removingProfileId: string | null;
  handleSetPrimaryProfile: (profileId: string) => Promise<void>;
  settingPrimaryId: string | null;
  handleRemoveProfile: (profileId: string) => Promise<void>;
}) {
  return (
    <>
      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
          <Card className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {(() => {
                    const RoleIcon = roleIcons[selectedUser.role] || User;
                    return <RoleIcon className="h-5 w-5 text-primary" />;
                  })()}
                  User Details
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowViewModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="rounded-full bg-primary/10 p-4">
                  {(() => {
                    const RoleIcon = roleIcons[selectedUser.role] || User;
                    return <RoleIcon className="h-8 w-8 text-primary" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedUser.name}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Role</p>
                  <Badge variant="outline" className={`mt-1 ${roleColors[selectedUser.role]}`}>
                    {roleLabels[selectedUser.role] || selectedUser.role}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge
                    variant="outline"
                    className={`mt-1 ${
                      selectedUser.invite_status === 'active'
                        ? 'bg-green-500/10 text-green-500 border-green-500/20'
                        : selectedUser.invite_status === 'invited'
                          ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          : 'bg-red-500/10 text-red-500 border-red-500/20'
                    }`}
                  >
                    {selectedUser.invite_status === 'active' && 'Active'}
                    {selectedUser.invite_status === 'invited' && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Invited
                      </span>
                    )}
                    {selectedUser.invite_status === 'suspended' && 'Suspended'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Joined:</span>
                  <span>{formatDate(selectedUser.created_at)}</span>
                </div>
                {selectedUser.country_code && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Country:</span>
                    <span>{selectedUser.country_code}</span>
                  </div>
                )}
                {selectedUser.agent && (
                  <div className="flex items-center gap-2 text-sm">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Territory:</span>
                    <span>{selectedUser.agent.territory_name || 'N/A'}</span>
                  </div>
                )}
                {/* Phone (HQ-001) */}
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Phone:</span>
                  {editingPhone ? (
                    <div className="flex items-center gap-1 flex-1">
                      <Input
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+44 7700 900000"
                        className="h-7 text-sm"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={handleSavePhone}
                        disabled={isSavingPhone}
                      >
                        {isSavingPhone ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Check className="h-3 w-3 text-green-500" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditingPhone(false)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="cursor-pointer hover:text-primary flex items-center gap-1 bg-transparent p-0"
                      onClick={() => {
                        setPhoneInput(userPhone || '');
                        setEditingPhone(true);
                      }}
                    >
                      {userPhone || 'Not set'}
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>

              {/* Agent Details (HQ-001) + T2-2: Editable Commission Rates */}
              {agentDetails && (
                <div className="space-y-2 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-primary" />
                      Commission Rates
                    </span>
                    {!editingCommission && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCommissionEquipInput(
                            agentDetails.commission_rate_equipment != null
                              ? (agentDetails.commission_rate_equipment * 100).toFixed(0)
                              : ''
                          );
                          setCommissionSubsInput(
                            agentDetails.commission_rate_subs != null
                              ? (agentDetails.commission_rate_subs * 100).toFixed(0)
                              : ''
                          );
                          setEditingCommission(true);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {editingCommission ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Equipment %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={commissionEquipInput}
                            onChange={(e) => setCommissionEquipInput(e.target.value)}
                            placeholder="15"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Subscriptions %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={commissionSubsInput}
                            onChange={(e) => setCommissionSubsInput(e.target.value)}
                            placeholder="10"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveCommission}
                          disabled={isSavingCommission}
                        >
                          {isSavingCommission ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : null}
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingCommission(false)}
                          disabled={isSavingCommission}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Equipment</p>
                        <p className="text-lg font-semibold">
                          {agentDetails.commission_rate_equipment != null
                            ? `${(agentDetails.commission_rate_equipment * 100).toFixed(0)}%`
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Subscriptions</p>
                        <p className="text-lg font-semibold">
                          {agentDetails.commission_rate_subs != null
                            ? `${(agentDetails.commission_rate_subs * 100).toFixed(0)}%`
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Details (HQ-001) */}
              {paymentDetails && (
                <div className="space-y-2 pt-4 border-t">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Payment Details
                  </span>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Stripe Customer</p>
                      <p className="text-sm font-mono">
                        {paymentDetails.stripe_customer_id
                          ? `cus_***${paymentDetails.stripe_customer_id.slice(-4)}`
                          : 'None'}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Subscription</p>
                      <Badge
                        variant="outline"
                        className={`mt-1 ${
                          paymentDetails.subscription_status === 'active'
                            ? 'bg-green-500/10 text-green-500 border-green-500/20'
                            : paymentDetails.subscription_status === 'past_due'
                              ? 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                              : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                        }`}
                      >
                        {paymentDetails.subscription_status || 'None'}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Role Profiles Section (HQ-012) */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Role Profiles</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAddProfileType('');
                      setHqAdminConfirmText('');
                      setAddProfileTerritoryName('');
                      setAddProfileAffiliateId('');
                      setAddProfileMemberRole('member');
                      setShowAddProfileModal(true);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Role
                  </Button>
                </div>

                {isLoadingProfiles ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : userProfiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No profiles</p>
                ) : (
                  <div className="space-y-2">
                    {userProfiles.map((profile) => {
                      const ProfileIcon = profileTypeIcons[profile.profile_type] || User;
                      const colorClass =
                        profileTypeColors[profile.profile_type] ||
                        'bg-gray-500/10 text-gray-400 border-gray-500/20';
                      const isSuspended = !profile.is_active;
                      const activeProfileCount = userProfiles.filter((p) => p.is_active).length;
                      return (
                        <div
                          key={profile.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            isSuspended ? 'bg-red-500/5 border border-red-500/20' : 'bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div
                              className={`rounded-md p-1.5 ${isSuspended ? 'bg-red-500/10 text-red-500 border-red-500/20' : colorClass}`}
                            >
                              <ProfileIcon className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p
                                className={`font-medium text-sm truncate ${isSuspended ? 'line-through opacity-60' : ''}`}
                              >
                                {getProfileDisplayLabel(profile)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {isSuspended ? (
                                  <span className="text-red-400">Suspended</span>
                                ) : (
                                  profileTypeLabels[profile.profile_type] || profile.profile_type
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            {isSuspended ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-xs h-7 px-2 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                onClick={() => handleReactivateProfile(profile.id)}
                                disabled={removingProfileId === profile.id}
                                title="Reactivate this role"
                              >
                                {removingProfileId === profile.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Reactivate
                                  </>
                                )}
                              </Button>
                            ) : (
                              <>
                                {profile.is_primary ? (
                                  <Badge
                                    variant="outline"
                                    className="bg-primary/10 text-primary border-primary/20 text-xs"
                                  >
                                    <Star className="h-3 w-3 mr-0.5" />
                                    Primary
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs h-7 px-2 text-muted-foreground hover:text-primary"
                                    onClick={() => handleSetPrimaryProfile(profile.id)}
                                    disabled={settingPrimaryId === profile.id}
                                  >
                                    {settingPrimaryId === profile.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      'Set Primary'
                                    )}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                                  onClick={() => handleRemoveProfile(profile.id)}
                                  disabled={
                                    removingProfileId === profile.id || activeProfileCount <= 1
                                  }
                                  title={
                                    activeProfileCount <= 1
                                      ? 'Cannot suspend last active role'
                                      : 'Suspend this role'
                                  }
                                >
                                  {removingProfileId === profile.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Ban className="h-3 w-3" />
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
